/* SchemaForm helper for single field rendering */
import React, { useState, useEffect } from "react";
import lodash from "lodash";
import moment from "moment";
import { useGetCall } from "seed/api";
import { LazyComponent } from "seed/helpers";
import { HelpComponent } from "components/util/schema_form/util/help_util";
import * as Fields from "./SchemaForm.lib.fields";
import SchemaRender from "./SchemaForm.sub.SchemaRender.view";


const FieldRender = ({
  input,
  appId,
  actionId,
  flowId,
  nodeId,
  parentId,
  executionId,
  sectionIdx,
  values,
  parentKey,
  isDeveloperMode,
  isReadOnly,
  isCommentEnabled,
  setFieldValue,
  setValues,
}) => {

  // Common props
  const name = parentKey == "" ? input.name : parentKey + "." + input.name;
  const type = input.type;
  const label = input.label ?? "";
  const value = lodash.get(values, name);
  const hidden = input?.hidden ?? false;
  const required = input?.required ?? !hidden;
  const disabled = input?.disabled ?? isReadOnly;
  const multiple = input?.multiple ?? false;
  const caption = input.caption ?? "";
  const helpMessage = input?.help_message;
  const placeholder = input?.placeholder;
  const improvementPrompt = input?.improvement_prompt;
  const enableComments = input?.enable_comments ?? isCommentEnabled;
  const defaultRawValue = input?.default;
  const developerOnly = input?.developer_only ?? false;
  const [defaultValue, setDefaultValue] = useState(null);

  // Select, text and variable props
  const openText = input?.open_text ?? false;
  const textType = input?.text_type ?? "general";
  const rows = input?.rows ?? 5;

  // Variable field props
  const acceptTypes = input?.accept_types ?? "all";
  const selectorFilters = input?.selector_filters ?? [];
  const supportIterables = input?.support_iterables ?? false;
  const enableAttributes = input?.enable_attributes ?? true;
  const fieldMappers = input?.field_mappers ?? [];
  const saveCollectionColumns = input?.save_collection_columns;
  const showCollectionCondition = input?.show_collection_condition ?? true;
  const showCollectionAddButton = input?.show_collection_add_button ?? false;
  const showConstants = input?.show_constants ?? false;
  const showNodeAddButton = input?.show_add_node_button ?? false;
  const displayAttributesManually = input?.display_attributes_manually ?? false;

  // Select field props
  const options = input?.options ?? [];
  const collectionOptions = input?.collection_options ?? [];
  const dynamicOptions = input?.dynamic_options ?? {};
  const dynamicDependencies = input?.dynamic_dependencies ?? [];

  // Hour props
  const minHour = input?.min ?? null;
  const maxHour = input?.max ?? null;

  // Date and DateTime props
  const minDate = input?.min ?? null;
  const maxDate = input?.max ?? null;

  // Number props
  const allowDecimals = input?.allow_decimals ?? false;
  const minValue = input?.min ?? null;
  const maxValue = input?.max ?? null;
  const step = input?.step ?? 1;

  // File field props
  const multipleFiles = input?.multiple_files ?? false;
  const acceptFiles = input?.accept_files ?? "*";

  // Rich Text props
  const showStyles = input?.show_styles ?? false;
  const showReferenceCaption = input?.show_reference_caption ?? true;

  // Schema Builder props
  const schemaButtonText = input?.schema_button_text;
  const schemaFieldsPlaceholder = input?.schema_fields_placeholder;
  const schemaInitialMessageRef = input?.schema_initial_message_ref;
  const schemaPreviewType = input?.schema_preview_type;

  // Account Picker props
  const integrationNames = input?.integration_names ?? [];
  const defaultAccountName = input?.default_account_name;
  const addAccountButtonText = input?.add_account_button_text;

  // Filter props
  const valueAAttrs = input?.value_a_attrs;
  const valueBAttrs = input?.value_b_attrs;
  const valueAPlaceholder = input?.value_a_placeholder
  const valueBPlaceholder = input?.value_b_placeholder
  const valueADefault = input?.value_a_default
  const valueBDefault = input?.value_b_default
  const conditionPlaceholder = input?.condition_placeholder
  const conditionDefault = input?.condition_default

  // Match props
  const showSaveAction = input?.show_save_action ?? false;
  const valueASource = input?.value_a_source
  const valueBSource = input?.value_b_source
  const valueAHeader = input?.value_a_header
  const valueBHeader = input?.value_b_header
  const valueAReference = input?.value_a_reference
  const valueBReference = input?.value_b_reference

  // Button props
  const buttonType = input?.button_type ?? "CALL";
  const buttonAction = input?.button_action ?? "";
  const buttonText = input?.button_text ?? "";
  const buttonStyle = input?.button_style ?? "outline";
  const buttonCallback = input?.button_callback ?? (() => { });
  const buttonFile = input?.button_file ?? "";

  // Label props
  const labelText = input?.label_text ?? "";
  const labelFontStyle = input?.label_font_style ?? "normal"
  const labelFontSize = input?.label_font_size ?? "normal"

  // Select props
  const selectType = input?.select_type ?? "dropdown";
  const multipleSelect = input?.multiple_select ?? false;

  // Confirm props
  const confirmLabel = input?.confirm_label ?? "Confirmar";
  const rejectLabel = input?.reject_label ?? "Rechazar";

  // Phone props
  const defaultCountry = input?.default_country ?? "mx";
  const allowedCountries = input?.allowed_countries ?? null;
  const allowedRegions = input?.allowed_regions ?? null;

  // Asset props
  const assetType = input?.asset_type ?? "file";
  const assetValue = input?.asset_value;

  // Location props
  const buttonTextLocation = input?.button_text ?? "Usar mi ubicación";
  const buttonStyleLocation = input?.button_style ?? "filled";

  // Color Picker props
  const openColor = input?.open_color ?? false;
  const colorOptions = input?.color_options ?? [];

  // Table Field props
  const dataSource = input?.data_source ?? null;
  const dataColumns = input?.data_columns ?? [];

  // Selector Field props
  const dataValues = input?.data_values ?? [];
  const dataStructure = input?.data_structure ?? [];
  const dataMultiple = input?.data_multiple ?? false;

  // Data Viewer props
  const dataName = input?.data_name ?? "";

  // Recursive select props
  const optionsCall = input?.options_call ?? "";
  const selectCall = input?.select_call ?? "";

  // Smart Text props
  const objective = input?.objective ?? "";
  const depth = input?.depth ?? 5;
  const examples = input?.examples;
  const hiddenId = "section-hidden-" + name.replaceAll("[", "").replaceAll("]", "").replaceAll(".", "-")

  // Excel props
  const excelStructure = input?.excel_structure ?? [];
  const singleInput = input?.single_input ?? false;
  const templateUrl = input?.template_url ?? "";
  const showAdvancedOptions = input?.show_advanced_options ?? true;
  const excelOffset = input?.excel_offset;
  const excelDataOffset = input?.excel_data_offset;
  const excelSheet = input?.excel_sheet;

  // Collection props
  const collectionButtonText = input?.collection_button_text;

  // Section props
  const fields = input?.fields ?? []
  const enableCollapse = input?.enable_collapse ?? false
  const collapsed = input?.collapsed ?? false


  // Initial value resolution
  const [callRealValue, reqRealValue] = useGetCall("/executions/get_query_result", {
    "query": defaultRawValue,
    "flow_id": flowId,
    "execution_id": executionId,
  }, {
    onCompleted: (data) => {
      setDefaultValue(data);
    },
    fetchPolicy: "no-cache"
  })

  useEffect(() => {
    // Init default value if rawValue starts with #, @ or &
    if ((defaultRawValue && typeof defaultRawValue === "string") &&
      (defaultRawValue.startsWith("#") || defaultRawValue.startsWith("@") || defaultRawValue.startsWith("&")))
      callRealValue();
    else {
      // Init standard default values
      if (defaultRawValue == "date_now") setDefaultValue(moment().format("YYYY-MM-DD"))
      else if (defaultRawValue == "datetime_now") setDefaultValue(moment().format("YYYY-MM-DD"))
      // By default set the raw value
      else setDefaultValue(defaultRawValue)
    }
  }, []);


  // Common props wrapper
  const commonProps = {
    name,
    label,
    type,
    value,
    required,
    hidden,
    disabled,
    multiple,
    caption,
    helpMessage,
    placeholder,
    enableComments,
    values,
    appId,
    actionId,
    flowId,
    nodeId,
    parentId,
    executionId,
    setFieldValue,
    input,
    sectionIdx,
    defaultValue,
  }

  // Variable props wrapper
  const variableProps = {
    acceptTypes,
    openText,
    textType,
    selectorFilters,
    supportIterables,
    enableAttributes,
    showCollectionAddButton,
    showCollectionCondition,
    saveCollectionColumns,
    showConstants,
    showNodeAddButton,
    displayAttributesManually,
    fieldMappers,
  }

  // Skip rendering if developer only and not in developer mode
  if (developerOnly && !isDeveloperMode) return <></>;

  // Render field based on type
  return (
    (() => {

      switch (type) {

        case "TEXT":
          return (
            <Fields.TextField
              {...commonProps}
              improvementPrompt={improvementPrompt}
              textType={textType}
            />
          );

        case "TEXT_AREA":
          return (
            <Fields.TextAreaField
              {...commonProps}
              improvementPrompt={improvementPrompt}
              rows={rows}
            />
          );

        case "NUMBER":
          return (
            <Fields.NumberField
              {...commonProps}
              allowDecimals={allowDecimals}
              minValue={minValue}
              maxValue={maxValue}
              step={step}
            />
          );

        case "LOCATION":
          return (
            <LazyComponent
              {...commonProps}
              buttonText={buttonTextLocation}
              buttonStyle={buttonStyleLocation}
              component={Fields.LocationField}
            />
          );

        case "SELECT":
          return (
            dynamicOptions.source == null ?
              <Fields.SelectField
                {...commonProps}
                options={options}
                collectionOptions={collectionOptions}
                openText={openText}
                selectType={selectType}
                multiple={multipleSelect}
              /> :
              <Fields.DynamicSelectField
                {...commonProps}
                dynamicOptions={dynamicOptions}
                dynamicDependencies={dynamicDependencies}
                openText={openText}
                selectType={selectType}
                multiple={multipleSelect}
              />
          )

        case "CONFIRM":
          return (
            <Fields.ConfirmField
              {...commonProps}
              confirmLabel={confirmLabel}
              rejectLabel={rejectLabel}
            />
          );

        case "PHONE":
          return (
            <LazyComponent
              {...commonProps}
              defaultCountry={defaultCountry}
              allowedCountries={allowedCountries}
              allowedRegions={allowedRegions}
              component={Fields.PhoneField}
            />
          );

        case "DATE":
          return (
            <Fields.TextField
              {...commonProps}
              textType={"date"}
              minDate={minDate}
              maxDate={maxDate}
            />
          );

        case "DATE_TIME":
          return (
            <Fields.TextField
              {...commonProps}
              textType={"datetime-local"}
              minDate={minDate}
              maxDate={maxDate}
            />
          );

        case "HOUR":
          return (
            <LazyComponent
              {...commonProps}
              minHour={minHour}
              maxHour={maxHour}
              component={Fields.HourField}
            />
          );

        case "FILE":
          return (
            <Fields.FileField
              {...commonProps}
              acceptFiles={acceptFiles}
              multipleFiles={multipleFiles}
            />
          );

        case "CHECKBOX":
          return (
            <Fields.CheckboxField
              {...commonProps}
            />
          );

        case "CODE":
          return (
            <LazyComponent
              {...commonProps}
              component={Fields.CodeField}
            />
          );

        case "VARIABLE":
          return (
            <LazyComponent
              {...commonProps}
              {...variableProps}
              component={Fields.VariableField}
            />
          );

        case "SCHEMA_BUILDER":
          return (
            <LazyComponent
              {...commonProps}
              schemaButtonText={schemaButtonText}
              schemaFieldsPlaceholder={schemaFieldsPlaceholder}
              schemaInitialMessageRef={schemaInitialMessageRef}
              schemaPreviewType={schemaPreviewType}
              component={Fields.SchemaBuilderField}
            />
          );

        case "MATCH":
          return (
            <LazyComponent
              {...commonProps}
              showSaveAction={showSaveAction}
              valueASource={valueASource}
              valueBSource={valueBSource}
              valueAPlaceholder={valueAPlaceholder}
              valueBPlaceholder={valueBPlaceholder}
              valueAHeader={valueAHeader}
              valueBHeader={valueBHeader}
              valueAReference={valueAReference}
              valueBReference={valueBReference}
              component={Fields.MatchField}
            />
          );

        case "ACCOUNT_PICKER":
          return (
            <LazyComponent
              {...commonProps}
              integrationNames={integrationNames}
              defaultAccountName={defaultAccountName}
              addAccountButtonText={addAccountButtonText}
              component={Fields.AccountPicker}
            />
          );

        case "LABEL":
          return (
            <Fields.LabelField
              {...commonProps}
              labelText={labelText}
              labelFontStyle={labelFontStyle}
              labelFontSize={labelFontSize}
            />
          );

        case "ASSET":
          return (
            <LazyComponent
              {...commonProps}
              assetType={assetType}
              assetValue={assetValue}
              component={Fields.AssetField}
            />
          );

        case "BUTTON":
          return (
            <Fields.ButtonField
              {...commonProps}
              buttonType={buttonType}
              buttonAction={buttonAction}
              buttonText={buttonText}
              buttonStyle={buttonStyle}
              buttonCallback={buttonCallback}
              buttonFile={buttonFile}
            />
          );

        case "RICH_TEXT":
          return (
            <LazyComponent
              {...commonProps}
              improvementPrompt={improvementPrompt}
              singleLine={true}
              showStyles={false}
              showReferenceCaption={showReferenceCaption}
              variableOptions={
                { ...variableProps }
              }
              component={Fields.RichText}
            />
          )

        case "RICH_TEXT_AREA":
          return (
            <LazyComponent
              {...commonProps}
              improvementPrompt={improvementPrompt}
              singleLine={false}
              rows={rows}
              showStyles={showStyles}
              showReferenceCaption={showReferenceCaption}
              variableOptions={
                { ...variableProps }
              }
              component={Fields.RichText}
            />
          )

        case "FILTER":
          return (
            <LazyComponent
              {...commonProps}
              valueAAttrs={valueAAttrs}
              valueBAttrs={valueBAttrs}
              valueAPlaceholder={valueAPlaceholder}
              valueBPlaceholder={valueBPlaceholder}
              valueADefault={valueADefault}
              valueBDefault={valueBDefault}
              conditionPlaceholder={conditionPlaceholder}
              conditionDefault={conditionDefault}
              component={Fields.FilterField}
            />
          )

        case "COLOR_PICKER":
          return (
            <Fields.ColorPickerField
              {...commonProps}
              openColor={openColor}
              colorOptions={colorOptions}
            />
          );

        case "DATA_VIEWER":
          return (
            <LazyComponent
              {...commonProps}
              dataName={dataName}
              component={Fields.DataViewerField}
            />
          );

        case "SELECTOR":
          return (
            <LazyComponent
              {...commonProps}
              dataValues={dataValues}
              dataStructure={dataStructure}
              dataMultiple={dataMultiple}
              component={Fields.SelectorField}
            />
          );

        case "RECURSIVE_SELECT":
          return (
            <Fields.RecursiveSelectField
              {...commonProps}
              optionsCall={optionsCall}
              selectCall={selectCall}
            />
          )

        case "SMART_TEXT":
          return (
            <Fields.SmartTextField
              {...commonProps}
              objective={objective}
              depth={depth}
              examples={examples}
            />
          )

        case "EXCEL":
          return (
            <Fields.ExcelField
              {...commonProps}
              excelStructure={excelStructure}
              excelOffset={excelOffset}
              excelDataOffset={excelDataOffset}
              excelSheet={excelSheet}
              singleInput={singleInput}
              templateUrl={templateUrl}
              showAdvancedOptions={showAdvancedOptions}
            />
          )

        case "COLLECTION_BUILDER":
          return (
            <LazyComponent
              {...commonProps}
              schema={value}
              collectionButtonText={collectionButtonText}
              component={Fields.CollectionBuilderField}
            />
          );

        case "SECTION":
          return (
            <div className="w-100 mt-1">
              {/* Section label and collapse control */}
              <a class="d-flex mb-1 w-100 justify-content-between"
                data-toggle="collapse"
                href={"#" + hiddenId}
                style={{ cursor: enableCollapse ? "pointer" : "default" }}>
                {label != "" &&
                  <span class="dropdown-header p-0">
                    <span style={{ color: "#777" }}>{label}</span>
                    {helpMessage != null ? <HelpComponent helpMessage={helpMessage} /> : null}
                  </span>}
                {enableCollapse && <i style={{ color: "#aaa" }} class="ml-1 fas fa-chevron-down"></i>}
              </a>

              <hr class="mt-2 w-100"
                style={{ border: "none", height: "1.5px", background: "#e7eaf3" }} />

              <div className="mt-4">
                <div id={enableCollapse ? hiddenId : ""}
                  className={collapsed ? "collapse" : "show"} >

                  {/* Render section fields recursively */}
                  <SchemaRender
                    schema={fields}
                    parentKey={`${name}`}
                    appId={appId}
                    actionId={actionId}
                    flowId={flowId}
                    nodeId={nodeId}
                    executionId={executionId}
                    parentId={parentId}
                    values={values}
                    isDeveloperMode={isDeveloperMode}
                    isReadOnly={isReadOnly}
                    isCommentEnabled={isCommentEnabled}
                    sectionIdx={sectionIdx}
                    setFieldValue={setFieldValue}
                    setValues={setValues}
                  />

                </div>
              </div>
            </div>
          );

        default:
          return <></>;
      }
    })())
}

export default FieldRender;
